import React, { useCallback, useMemo, type CSSProperties } from "react";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { triggerSelectionHaptic } from "../../../tools/haptics.tools";
import { type SavedListFilterTabId } from "../../../tools/savedItineraryDisplay.tools";

const TAB_TRANSITION_MS = 200;

/** Definition for one saved-list filter tab. */
export interface ItineraryListFilterTabDefinition {
    "id":    SavedListFilterTabId;
    "label": string;
}

interface ItineraryListFilterTabsProps {

    /** Enables dark palette variants for tab chrome. */
    "isDarkUi": boolean;

    /** Controlled active tab id. */
    "activeTabId": SavedListFilterTabId;

    /** Called when the user selects a different tab. */
    "onActiveTabChange": ( tabId: SavedListFilterTabId ) => void;

    /** Tab definitions to render (order preserved). */
    "tabs": readonly ItineraryListFilterTabDefinition[];

    /** Optional accessible name for the tab list. */
    "ariaLabel"?: string;

    /** Optional class names on the root wrapper. */
    "className"?: string;

    /** Stable DOM id for the tab list root. */
    "id"?: string;
}

/**
 * Pill segmented control for saved-itinerary list filters (MiniModal-inspired, tap-only).
 *
 * @param props Tab chrome props.
 * @returns Filter tab row.
 */
const ItineraryListFilterTabs: React.FC<ItineraryListFilterTabsProps> = ( {
    isDarkUi,
    activeTabId,
    onActiveTabChange,
    tabs,
    ariaLabel = "Filtrer les itinéraires sauvegardés",
    className = "",
    id,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const reducedMotion = useReducedMotion();

    const activeIndex = useMemo( (): number => {
        const index = tabs.findIndex( ( tab ) => tab.id === activeTabId );

        return index >= 0 ? index : 0;
    }, [ activeTabId, tabs ] );

    const indicatorTransition = reducedMotion
        ? "none"
        : `transform ${ TAB_TRANSITION_MS }ms cubic-bezier(0.2, 0.8, 0.2, 1)`;

    const indicatorStyle: CSSProperties = useMemo( () => {
        const tabCount = Math.max( tabs.length, 1 );
        const widthPercent = 100 / tabCount;

        return {
            "width":           `${ widthPercent }%`,
            "transform":       `translateX(${ activeIndex * 100 }%)`,
            "transition":      indicatorTransition,
            "backgroundColor": palette.selectorActiveBackgroundStrong,
        };
    }, [ activeIndex, indicatorTransition, palette.selectorActiveBackgroundStrong, tabs.length ] );

    /**
     * Handles pill tap and triggers selection haptic feedback.
     *
     * @param tabId Selected tab id.
     */
    const handleTabSelect = useCallback( ( tabId: SavedListFilterTabId ): void => {
        onActiveTabChange( tabId );
        void triggerSelectionHaptic();
    }, [ onActiveTabChange ] );

    if ( tabs.length === 0 ) {
        return null;
    }

    return (
        <div
            className={className}
            id={id}
        >
            <div
                aria-label={ariaLabel}
                className="relative grid rounded-full border p-0.5"
                role="tablist"
                style={{
                    "borderColor":         palette.overlayBorder,
                    "backgroundColor":     palette.selectorIdleBackground,
                    "gridTemplateColumns": `repeat(${ tabs.length }, minmax(0, 1fr))`,
                }}
            >
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0.5 overflow-hidden rounded-full"
                >
                    <div
                        className="h-full rounded-full"
                        style={indicatorStyle}
                    />
                </div>
                {tabs.map( ( tab ) => {
                    const isSelected = tab.id === activeTabId;

                    return (
                        <button
                            aria-selected={isSelected}
                            className="relative z-10 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide"
                            key={tab.id}
                            onClick={() => {
                                handleTabSelect( tab.id );
                            }}
                            role="tab"
                            style={{ "color": isSelected ? palette.selectorActiveText : palette.selectorIdleText }}
                            type="button"
                        >
                            {tab.label}
                        </button>
                    );
                } )}
            </div>
        </div>
    );
};

export default ItineraryListFilterTabs;
